import { useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';

import { request, ENV, isEthAddress } from 'utils';

type RefreshConfig = {
  auto_refresh_strategy: 'snapshot' | 'chain';
  manual_refresh_strategy: 'snapshot' | 'chain';
  snapshot_elapse: number;
  can_manual_refresh: boolean;
  can_load_24h_history: boolean;
};

export type ViewConfig = {
  portfolio: {
    other: RefreshConfig;
    self: RefreshConfig;
  };
  history: {
    can_load: boolean;
  };
  level: number;
  nft: {
    can_load: boolean;
  };
  unavailable_chains?: string[];
};

const configUrl = `/api/${ENV.isProduction ? 'config' : 'alpha-config'}.json`;

const defaultConfig: ViewConfig = {
  portfolio: {
    self: {
      snapshot_elapse: 600,
      auto_refresh_strategy: 'snapshot',
      manual_refresh_strategy: 'chain',
      can_manual_refresh: true,
      can_load_24h_history: true,
    },
    other: {
      snapshot_elapse: 3600,
      auto_refresh_strategy: 'snapshot',
      manual_refresh_strategy: 'chain',
      can_manual_refresh: true,
      can_load_24h_history: true,
    },
  },
  level: 0,
  nft: { can_load: true },
  history: { can_load: true },
};

// 全局配置
export const useConfig = () => {
  const query = useQuery(['profile/config'], () =>
    request
      .get<ViewConfig>(configUrl, null, {
        timeout: 5000,
        base_url: 'https://static.debank.com',
        noSession: true,
        noSource: true,
        noLng: true,
        nonce: false,
      })
      .then(data => ({ ...defaultConfig, ...data }))
      .catch(() => defaultConfig),
  );

  return query;
};

// 单个地址配置
export const usePersonalConfig = (address: string) => {
  const url = '/user/config';

  const query = useQuery(
    [url, address],
    () =>
      request
        .get<{ limit_strategy: ViewConfig }>(
          url,
          { id: address },
          {
            timeout: 5000,
            noSession: true,
            noSource: true,
          },
        )
        .then(data => ({ ...defaultConfig, ...data?.limit_strategy }))
        .catch(() => defaultConfig),
    {
      enabled: isEthAddress(address),
    },
  );

  return query;
};

// 取 level 比较大的（降级程度更深的）
export const useMergedConfig = (address: string) => {
  const {
    data: globalConfig,
    // 因为这个 hook 有可能被 use_total_balance 触发，所以只需要 loading(从无到有)，不用看 fetching(包括 loading 和 从有到有)
    isLoading: isGlobalConfigLoading,
    refetch: refetchGlobalConfig,
  } = useConfig();

  const {
    data: personalConfig,
    isLoading: isPersonalConfigLoading,
    refetch: refetchPersonalConfig,
  } = usePersonalConfig(address);

  const data = useMemo(() => {
    if (isGlobalConfigLoading || isPersonalConfigLoading) {
      return;
    }

    return personalConfig && personalConfig.level > globalConfig!.level
      ? personalConfig
      : globalConfig;
  }, [globalConfig, personalConfig, isGlobalConfigLoading, isPersonalConfigLoading]);

  const isLoading = useMemo(() => {
    return isGlobalConfigLoading || isPersonalConfigLoading;
  }, [isGlobalConfigLoading, isPersonalConfigLoading]);

  const refetch = useCallback(() => {
    refetchGlobalConfig();
    refetchPersonalConfig();
  }, [refetchGlobalConfig, refetchPersonalConfig]);

  return {
    data,
    isLoading,
    refetch,
  };
};
